import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, Camera, Loader2, Trash2, Sparkles,
} from "lucide-react";

// Cleaning-log flow shown after the operator picks "Log cleaning" on the scan
// landing page. Reviews the protocol checklist, captures outcome + notes +
// optional photos, and flags any issue to supervisors on submit.
export default function CleaningLogForm({ equipmentId, equipmentName, siteName, onBack, onRescan, onHome }) {
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState("");
  const [outcome, setOutcome] = useState("completed");
  const [issueDesc, setIssueDesc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [issuePhotos, setIssuePhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/cleaning/protocol/${equipmentId}`);
        setProtocol(data.protocol);
      } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Could not load protocol."); }
      finally { setLoading(false); }
    })();
  }, [equipmentId]);

  const steps = protocol?.steps || [];
  const allChecked = steps.length > 0 && steps.every((s) => checked[s.key]);
  const toggleAll = () => {
    if (allChecked) setChecked({});
    else setChecked(Object.fromEntries(steps.map((s) => [s.key, true])));
  };

  const uploadPhoto = async (file, kind, setter) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cleaning/upload-photo?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setter((prev) => [...prev, data.url]);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Photo upload failed."); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (outcome === "issue" && !issueDesc.trim()) { setErr("Describe the issue you found."); return; }
    setBusy(true);
    try {
      await api.post("/cleaning/logs", {
        equipment_id: equipmentId,
        notes,
        outcome,
        completed_step_keys: steps.filter((s) => checked[s.key]).map((s) => s.key),
        photos,
        issue_description: outcome === "issue" ? issueDesc : "",
        issue_photos: outcome === "issue" ? issuePhotos : [],
      });
      setSuccess(true);
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Submit failed."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 opacity-70" data-testid="cleaning-loading">Loading protocol…</div>;

  if (success) {
    return (
      <div className="max-w-lg vb-fade-in p-6" data-testid="cleaning-success">
        <div className={`vb-pill ${outcome === "issue" ? "warn" : "ok"} mb-4`}>
          {outcome === "issue" ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
          {outcome === "issue" ? " Cleaning logged · issue flagged to supervisor" : " Cleaning logged"}
        </div>
        <h2 className="vb-heading text-2xl mb-2" style={{ color: "var(--vb-text-2)" }}>Thanks — recorded</h2>
        <p className="opacity-70 mb-6 text-sm">{equipmentName}{siteName ? ` · ${siteName}` : ""}</p>
        <div className="flex gap-3 flex-wrap">
          <button className="vb-btn" onClick={onRescan} data-testid="cleaning-scan-next"><Camera size={16} /> Scan next equipment</button>
          <button className="vb-btn ghost" onClick={onHome} data-testid="cleaning-done">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg vb-fade-in" data-testid="cleaning-form">
      <button onClick={onBack} className="vb-btn ghost mb-3" data-testid="cleaning-back"><ArrowLeft size={14} /> Back</button>

      <div className="vb-card p-5 mb-4">
        <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Log cleaning</div>
        <div className="vb-heading text-2xl mb-1" style={{ color: "var(--vb-text-2)" }} data-testid="cleaning-eq-name">{equipmentName}</div>
        {siteName && <div className="text-sm opacity-80">{siteName}</div>}
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="cleaning-error">{err}</div>}

      <form onSubmit={submit} className="vb-card p-5 grid gap-5">
        {steps.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
                {protocol.title}
              </label>
              <button type="button" className="text-xs vb-link" onClick={toggleAll} data-testid="cleaning-toggle-all">
                {allChecked ? "Clear all" : "Mark all done"}
              </button>
            </div>
            <div className="space-y-2" data-testid="cleaning-steps">
              {steps.map((s, i) => (
                <label key={s.key} className="flex items-start gap-2.5 text-sm cursor-pointer p-2 rounded" style={{ background: "var(--vb-bg-2)" }}>
                  <input type="checkbox" checked={!!checked[s.key]} className="mt-0.5"
                    onChange={(e) => setChecked((c) => ({ ...c, [s.key]: e.target.checked }))}
                    data-testid={`cleaning-step-${i}`} />
                  <span>{s.text}</span>
                </label>
              ))}
            </div>
          </div>
        ) : (
          <div className="vb-pill" style={{ background: "var(--vb-bg-2)" }}>
            <Sparkles size={13} /> No detailed protocol configured — confirm the equipment has been cleaned below.
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Notes (optional)</label>
          <textarea className="vb-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cleaning-notes" />
        </div>

        <PhotoRow label="Photos (optional)" photos={photos} setPhotos={setPhotos} kind="cleaning"
          upload={uploadPhoto} uploading={uploading} testid="cleaning" />

        <div>
          <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>Outcome</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setOutcome("completed")} className={`vb-btn ${outcome === "completed" ? "" : "ghost"}`} style={{ flex: 1 }} data-testid="cleaning-outcome-completed">
              <CheckCircle2 size={15} /> Completed
            </button>
            <button type="button" onClick={() => setOutcome("issue")} className={`vb-btn ${outcome === "issue" ? "warn" : "ghost"}`} style={{ flex: 1 }} data-testid="cleaning-outcome-issue">
              <AlertTriangle size={15} /> Issue found
            </button>
          </div>
        </div>

        {outcome === "issue" && (
          <div className="grid gap-4 p-3 rounded" style={{ background: "var(--vb-bg-2)" }} data-testid="cleaning-issue-block">
            <div>
              <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-error)" }}>Describe the issue *</label>
              <textarea className="vb-input" rows={2} value={issueDesc} onChange={(e) => setIssueDesc(e.target.value)}
                placeholder="e.g. Door seal torn, rust on shelf…" data-testid="cleaning-issue-desc" />
            </div>
            <PhotoRow label="Issue photos" photos={issuePhotos} setPhotos={setIssuePhotos} kind="issue"
              upload={uploadPhoto} uploading={uploading} testid="cleaning-issue" />
            <p className="text-xs opacity-70 m-0">This will be flagged to your supervisor to review and close.</p>
          </div>
        )}

        <button type="submit" className={`vb-btn ${outcome === "issue" ? "warn" : ""}`} disabled={busy || uploading} data-testid="cleaning-submit">
          {busy ? "Saving…" : outcome === "issue" ? "Log cleaning & report issue" : "Confirm cleaning done"}
        </button>
      </form>
    </div>
  );
}

function PhotoRow({ label, photos, setPhotos, kind, upload, uploading, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>{label}</label>
      <div className="flex flex-wrap gap-2 items-center">
        {photos.map((url, i) => (
          <div key={i} className="relative" style={{ width: 64, height: 64 }}>
            <img src={url} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8 }} />
            <button type="button" onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))}
              className="absolute -top-2 -right-2 rounded-full" style={{ background: "var(--vb-error)", color: "#fff", width: 20, height: 20, lineHeight: 0 }}
              data-testid={`${testid}-photo-remove-${i}`}><Trash2 size={11} /></button>
          </div>
        ))}
        <label className="vb-btn ghost sm" style={{ cursor: "pointer" }} data-testid={`${testid}-photo-add`}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />} Add photo
          <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={(e) => { upload(e.target.files?.[0], kind, setPhotos); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}
